import { useCallback } from 'react';
import { AnimatePresence } from 'motion/react';
import { Dropzone } from '@/shared/ui/Dropzone/Dropzone';
import { FileListItem } from '@/shared/ui/FileListItem';
import { UploadFileIcon } from '@/shared/ui/Icons';
import type { ExtractFromDocumentTabProps } from './shared.types';
import { useTranslation } from 'react-i18next';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ACCEPTED_FORMATS = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/plain': ['.txt'],
  'application/rtf': ['.rtf'],
};

export const ExtractFromDocumentTab = ({
  files,
  onAddFiles,
  onRemoveFile,
}: ExtractFromDocumentTabProps) => {
  const { t } = useTranslation('analysis');

  const handleDrop = useCallback(
    (droppedFiles: File[]) => {
      const validFiles = droppedFiles.filter((file) => file.size <= MAX_FILE_SIZE);
      if (validFiles.length > 0) {
        onAddFiles(validFiles);
      }
    },
    [onAddFiles],
  );

  return (
    <div className="flex flex-col gap-4">
      <Dropzone
        onDrop={handleDrop}
        acceptedFormats={ACCEPTED_FORMATS}
        title={t('analysis.extractFromDocumentTab.dropDocumentsHere')}
        description={t('analysis.extractFromDocumentTab.pdfDocDocxTxtRtf')}
        size="small"
        className="bg-background"
        icon={<UploadFileIcon className="size-5 text-primary" />}
      />
      {files.length > 0 && (
        <div className="flex flex-col gap-2">
          <AnimatePresence>
            {files.map((file, index) => (
              <FileListItem
                key={`${file.name}-${file.size || 0}`}
                fileName={file.name}
                onRemove={() => onRemoveFile(file)}
                delay={index * 0.05}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
